import { notifyManager } from '@tanstack/react-query';

import { useScopeUrl } from '@/hooks/use-scope-url';
import { selectAccount } from '@/queries/accounts/selectors';
import { queryClient } from '@/queries/client';
import { queryKeys } from '@/queries/keys';
import { normalizeStatus } from '@/queries/statuses/normalize';
import { useContextStore } from '@/stores/contexts';
import { setStatusUnavailable, isStatusDeleted } from '@/stores/status-availability';

import { scopedQueryKey } from '../query';

import { observeStreamStatus } from './status-observations';

import type {
  Account as BaseAccount,
  Group as BaseGroup,
  Poll as BasePoll,
  Relationship as BaseRelationship,
  Status as BaseStatus,
  StatusWithoutAccount,
  Translation,
} from 'pl-api';

type Status = (BaseStatus | StatusWithoutAccount) & {
  expectsCard?: boolean;
  accounts?: Array<BaseAccount>;
};

const isEmpty = (object: Record<string, any>) => !Object.values(object).some((value) => value);

interface ImportEntitiesEntities {
  accounts?: Array<BaseAccount | undefined | null>;
  groups?: Array<BaseGroup | undefined | null>;
  polls?: Array<BasePoll | undefined | null>;
  statuses?: Array<Status | undefined | null>;
  relationships?: Array<BaseRelationship | undefined | null>;
}

interface ImportEntitiesOptions {
  // Whether to replace existing entities. Set to false when working with potentially outdated data. Currently, only implemented for accounts.
  override?: boolean;
  withParents?: boolean;
  idempotencyKey?: string;
  // Reject statuses superseded while a REST request was in flight.
  isStatusCurrent?: (id: string) => boolean;
  onStatusImported?: (id: string) => void;
  streaming?: boolean;
}

const importEntities = (
  scopeUrl: string,
  entities: ImportEntitiesEntities,
  options: ImportEntitiesOptions = {
    withParents: true,
  },
) => {
  const override = options.override ?? true;

  const accounts: Record<string, BaseAccount> = {};
  const groups: Record<string, BaseGroup> = {};
  const polls: Record<string, BasePoll> = {};
  const relationships: Record<string, BaseRelationship> = {};
  const statuses: Record<string, Status> = {};
  const translations: Record<string, Record<string, Translation>> = {};

  const processAccount = (account: BaseAccount, withSelf = true) => {
    if (!override && selectAccount(account.id, scopeUrl)) return;

    if (withSelf) accounts[account.id] = account;

    if (account.moved) processAccount(account.moved);
    if (account.relationship) relationships[account.relationship.id] = account.relationship;
  };

  const processStatus = (status: Status, withSelf = true) => {
    // Skip broken statuses
    if (
      status.scheduled_at !== null ||
      isStatusDeleted(scopeUrl, status.id) ||
      options.isStatusCurrent?.(status.id) === false
    )
      return;

    setStatusUnavailable(scopeUrl, status.id, false);
    if (withSelf) statuses[status.id] = status;

    if (status.account) {
      processAccount(status.account);
    }

    if (status.accounts) {
      for (const account of status.accounts) processAccount(account);
    }

    if (status.quote && 'quoted_status' in status.quote && status.quote.quoted_status)
      processStatus(status.quote.quoted_status);
    if (status.reblog) processStatus(status.reblog);
    if (status.poll) polls[status.poll.id] = status.poll;
    if (status.group) groups[status.group.id] = status.group;
    if (status.translation) {
      if (!translations[status.id]) translations[status.id] = {};
      translations[status.id][status.translation.language] = status.translation;
    }
  };

  if (options.withParents) {
    entities.groups?.forEach((group) => group && (groups[group.id] = group));
    entities.polls?.forEach((poll) => poll && (polls[poll.id] = poll));
    entities.relationships?.forEach(
      (relationship) => relationship && (relationships[relationship.id] = relationship),
    );
  }

  entities.accounts?.forEach((account) => account && processAccount(account, options.withParents));

  if (entities.statuses?.length === 1 && entities.statuses[0] && options.idempotencyKey) {
    const status = entities.statuses[0];
    if (isStatusDeleted(scopeUrl, status.id) || options.isStatusCurrent?.(status.id) === false)
      return;
    useContextStore.getState().actions.importStatus(status, options.idempotencyKey);
    const oldStatus = queryClient.getQueryData(
      scopedQueryKey(queryKeys.statuses.show(status.id), scopeUrl),
    );
    const normalized = normalizeStatus(status, oldStatus);
    queryClient.setQueryData(
      scopedQueryKey(queryKeys.statuses.show(status.id), scopeUrl),
      normalized,
    );
    if (options.streaming) observeStreamStatus(scopeUrl, status.id);
    options.onStatusImported?.(status.id);
    processStatus(status, false);
  } else {
    entities.statuses?.forEach((status) => status && processStatus(status, options.withParents));
  }

  notifyManager.batch(() => {
    if (!isEmpty(accounts)) {
      for (const account of Object.values(accounts)) {
        queryClient.setQueryData(
          scopedQueryKey(queryKeys.accounts.lookup(account.acct.toLowerCase()), scopeUrl),
          account.id,
        );
        queryClient.setQueryData(
          scopedQueryKey(queryKeys.accounts.show(account.id), scopeUrl),
          account,
        );
      }
    }
    if (!isEmpty(groups))
      for (const group of Object.values(groups)) {
        queryClient.setQueryData(scopedQueryKey(queryKeys.groups.show(group.id), scopeUrl), group);
        if (group.relationship) {
          queryClient.setQueryData(
            scopedQueryKey(queryKeys.groupRelationships.show(group.id), scopeUrl),
            group.relationship,
          );
        }
      }
    if (!isEmpty(polls)) {
      for (const poll of Object.values(polls)) {
        queryClient.setQueryData(
          scopedQueryKey(queryKeys.statuses.polls.show(poll.id), scopeUrl),
          poll,
        );
      }
    }
    if (!isEmpty(relationships)) {
      for (const relationship of Object.values(relationships)) {
        queryClient.setQueryData(
          scopedQueryKey(queryKeys.accountRelationships.show(relationship.id), scopeUrl),
          relationship,
        );
      }
    }
    if (!isEmpty(statuses))
      useContextStore.getState().actions.importStatuses(Object.values(statuses));

    if (!isEmpty(statuses)) {
      for (const status of Object.values(statuses)) {
        const oldStatus = queryClient.getQueryData(
          scopedQueryKey(queryKeys.statuses.show(status.id), scopeUrl),
        );
        const normalized = normalizeStatus(status, oldStatus);
        queryClient.setQueryData(
          scopedQueryKey(queryKeys.statuses.show(status.id), scopeUrl),
          normalized,
        );
        if (options.streaming) observeStreamStatus(scopeUrl, status.id);
        options.onStatusImported?.(status.id);
      }
    }
    if (!isEmpty(translations)) {
      for (const [statusId, translationsByLanguage] of Object.entries(translations)) {
        for (const [language, translation] of Object.entries(translationsByLanguage)) {
          queryClient.setQueryData(
            scopedQueryKey(queryKeys.statuses.translations(statusId, language), scopeUrl),
            translation,
          );
        }
      }
    }
  });
};

const useImportEntities = () => {
  const scopeUrl = useScopeUrl();

  return (entities: ImportEntitiesEntities, options?: ImportEntitiesOptions) => {
    importEntities(scopeUrl, entities, options);
  };
};

export { importEntities, useImportEntities };
